/**
 * API client for user forest management endpoints.
 *
 * Uses the shared `apiClient` from client.ts which automatically attaches
 * the JWT Authorization header via the `attachAuthToken` interceptor.
 * See the canonical transport contract in client.ts.
 */
import { apiClient } from './client';

export interface PropertyDetail {
  property_id: string;
  /** Null when the server records no name for the property (metadata_available false). */
  display_name: string | null;
  municipality: string;
  total_area_ha: number;
  base_price?: number;
  stand_count?: number;
  preview_note?: string;
  has_access?: boolean;
  metadata_available: boolean;
}

/** Values served by Django ForestAccess and projected by the FastAPI gateway. */
export type UserForestAccessType = 'owner' | 'manager' | 'viewer' | 'purchased';

export interface UserForestGrantAcknowledgement {
  property_id: string;
  display_name: string | null;
  municipality: string;
  /** May be null when the property area is not yet resolved (real gateway shape). */
  total_area_ha: number | null;
  access_type: UserForestAccessType;
  granted_at: string;
  expires_at: string | null;
  purchase_price: number | null;
  metadata_available: boolean;
}

export interface TreeMeasurement {
  id?: number;
  forest_stand: number;
  tree_species_code: string;
  tree_species_name: string;
  diameter_cm: number;
  height_m?: number;
  gps_latitude?: number;
  gps_longitude?: number;
  measurement_method: string;
  notes?: string;
  measurement_date?: string;
  measured_by_username?: string;
}

export interface CustomOperation {
  id?: number;
  property_id: string;
  stand_id: string;
  operation_data_json: Record<string, unknown>;
  operation_date: string;
  is_completed?: boolean;
  completion_date?: string;
  notes?: string;
  created_by_username?: string;
}

export interface ForestAccessRecord {
  id: number;
  property_id: string;
  access_type: string;
  granted_at: string;
  expires_at?: string;
  purchase_price?: number;
  property_name: string;
  municipality: string;
}

export interface ForestStandDataResponse {
  property_id?: string;
  stand_id?: string;
  geometry?: unknown;
  properties?: Record<string, unknown>;
  [key: string]: unknown;
}

// API Client Class
export class UserForestsAPI {

  // Property Management
  async getProperty(propertyId: string): Promise<PropertyDetail> {
    const response = await apiClient.get<PropertyDetail>(`/api/v1/user-forests/properties/${propertyId}/`);
    return response.data;
  }

  // Free Add Grant (Mock Seam B real backend)
  // POST /api/v1/user-forests/add/ { property_id } → grant acknowledgement.
  // Idempotent: re-add returns the existing forest, NO second grant/charge.
  // NO toast side-effect: callers surface failures via the UI (Alert/FmToast),
  // never the console — keeping the grant flow smoke-clean.
  async addProperty(propertyId: string, options: { restore?: boolean } = {}): Promise<UserForestGrantAcknowledgement> {
    const response = await apiClient.post<UserForestGrantAcknowledgement>('/api/v1/user-forests/add/', {
      property_id: propertyId,
      ...(options.restore ? { restore: true } : {}),
    });
    return response.data;
  }

  // Forest Stand Data
  async getForestStandData(
    propertyId: string,
    standId: string,
    source: string = 'auto',
  ): Promise<ForestStandDataResponse> {
    const response = await apiClient.get<ForestStandDataResponse>(
      `/api/v1/user-forests/stands/${propertyId}/${standId}/`,
      { params: { source } },
    );
    return response.data;
  }

  // Tree Measurements
  async createTreeMeasurement(measurement: Omit<TreeMeasurement, 'id' | 'measured_by_username'>): Promise<TreeMeasurement> {
    const response = await apiClient.post<TreeMeasurement>('/api/v1/user-forests/measurements/', {
      ...measurement,
      measurement_date: measurement.measurement_date || new Date().toISOString(),
    });
    return response.data;
  }

  async getUserMeasurements(propertyId?: string, standId?: string): Promise<TreeMeasurement[]> {
    const params: Record<string, string> = {};
    if (propertyId) params.property_id = propertyId;
    if (standId) params.stand_id = standId;

    const response = await apiClient.get<TreeMeasurement[]>('/api/v1/user-forests/measurements/', { params });
    return response.data;
  }

  // Custom Operations
  async createCustomOperation(operation: Omit<CustomOperation, 'id' | 'created_by_username'>): Promise<CustomOperation> {
    const response = await apiClient.post<CustomOperation>('/api/v1/user-forests/operations/', operation);
    return response.data;
  }

  async getUserOperations(propertyId?: string, standId?: string): Promise<CustomOperation[]> {
    const params: Record<string, string> = {};
    if (propertyId) params.property_id = propertyId;
    if (standId) params.stand_id = standId;

    const response = await apiClient.get<CustomOperation[]>('/api/v1/user-forests/operations/', { params });
    return response.data;
  }

  // Access Records
  async getAccessRecords(): Promise<ForestAccessRecord[]> {
    const response = await apiClient.get<ForestAccessRecord[]>('/api/v1/user-forests/access/');
    return response.data;
  }
}

// Export singleton instance
export const userForestsAPI = new UserForestsAPI();
